import React, { useState, useMemo, useCallback } from 'react';
import {
  TextField,
  InputAdornment,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Button,
  Collapse,
  Checkbox,
  IconButton,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/**
 * Componente de selección de servicios/tiendas con búsqueda optimizada y lazy loading
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.servicios - Lista de servicios disponibles
 * @param {Array} props.selectedServicios - Lista de IDs o números de servicios seleccionados
 * @param {Function} props.onChange - Función a llamar cuando cambia la selección
 * @param {boolean} props.disabled - Indica si el componente está deshabilitado
 * @param {boolean} props.required - Indica si el campo es obligatorio
 * @param {boolean} props.loading - Indica si está cargando datos
 * @param {string} props.searchTerm - Término de búsqueda actual
 * @param {Function} props.setSearchTerm - Función para establecer el término de búsqueda
 * @param {boolean} props.hasMore - Indica si hay más datos para cargar
 * @param {boolean} props.isLoadingMore - Indica si está cargando más datos
 * @param {React.RefObject} props.observerRef - Referencia para el observer de scroll
 * @returns {JSX.Element} - Componente ServicioSearch
 */
const ServicioSearch = ({
  servicios = [],
  selectedServicios = [],
  onChange,
  disabled = false,
  required = false,
  loading = false,
  searchTerm = '',
  setSearchTerm,
  hasMore = false,
  isLoadingMore = false,
  observerRef,
  showDisplayToggle = false
}) => {
  const [open, setOpen] = useState(false);
  const [displayMode, setDisplayMode] = useState('nombre');

  const handleDisplayModeChange = (event, newMode) => {
    if (newMode !== null) {
      setDisplayMode(newMode);
    }
  };

  // Memoizar selectedServicios para evitar problemas de referencia
  const selectedArray = useMemo(() => {
    return Array.isArray(selectedServicios) ? selectedServicios : [];
  }, [selectedServicios]);

  // Filtrar servicios basados en el texto de búsqueda (con deduplicación)
  const filteredServicios = useMemo(() => {
    if (!servicios || servicios.length === 0) return [];
    
    // Primero filtrar por término de búsqueda
    const filtered = servicios.filter(servicio => {
      const searchLower = searchTerm.toLowerCase();
      // Búsqueda parcial en nombre, localización y cliente
      return (
        servicio.numTienda?.toString().toLowerCase().includes(searchLower) || 
        (servicio.nombreTienda && servicio.nombreTienda.toLowerCase().includes(searchLower)) ||
        (servicio.nombreCliente && servicio.nombreCliente.toLowerCase().includes(searchLower)) ||
        (servicio.localizacion && servicio.localizacion.toLowerCase().includes(searchLower))
      );
    });
    
    // Eliminar duplicados por numTienda o localizacion
    const seen = new Set();
    return filtered.filter(servicio => {
      const key = servicio.localizacion || servicio.numTienda?.toString();
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [servicios, searchTerm]);

  // Renderizar la etiqueta del servicio según el modo de visualización
  const renderServicioLabel = useCallback((servicio) => {
    const identifier = displayMode === 'localizacion' && servicio.localizacion 
      ? servicio.localizacion 
      : servicio.numTienda?.toString() || '';
    
    if (servicio.nombreTienda) {
      return `${identifier} - ${servicio.nombreTienda}`;
    }
    if (servicio.nombreCliente) {
      return `${identifier} (${servicio.nombreCliente})`;
    }
    return identifier;
  }, [displayMode]);

  // Manejar cambios en la selección de servicios individuales
  const handleServicioToggle = useCallback((servicioId, isChecked) => {
    console.log('🔍 ServicioSearch handleServicioToggle:', { servicioId, isChecked, selectedArray });
    const newSelectedServicios = isChecked
      ? [...selectedArray, servicioId]
      : selectedArray.filter(id => id !== servicioId);
    console.log('🔍 Nuevos servicios seleccionados:', newSelectedServicios);
    onChange(newSelectedServicios);
  }, [selectedArray, onChange]);

  // Manejar cambios en la búsqueda
  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, [setSearchTerm]);

  // Limpiar búsqueda
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, [setSearchTerm]);

  // Eliminar servicio seleccionado
  const handleDeleteServicio = useCallback((servicioId) => {
    const newSelectedServicios = selectedArray.filter(id => id !== servicioId);
    onChange(newSelectedServicios);
  }, [selectedArray, onChange]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Servicios {required && '*'}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          p: 2, 
          border: '1px solid #ddd', 
          borderRadius: 1,
          backgroundColor: '#f5f5f5'
        }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Cargando servicios...</Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (servicios.length === 0 && !loading) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Servicios {required && '*'}
        </Typography>
        <Box sx={{ 
          p: 2, 
          border: '1px solid #ddd', 
          borderRadius: 1,
          backgroundColor: '#f5f5f5',
          textAlign: 'center'
        }}>
          <Typography variant="body2" color="text.secondary">
            No hay servicios disponibles
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Servicios {required && '*'}
      </Typography>
      
      {/* Servicios seleccionados */}
      {selectedArray.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Servicios seleccionados ({selectedArray.length}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedArray.map((servicioId) => {
              const servicio = servicios.find(s => s.numTienda.toString() === servicioId);
              return (
                <Chip 
                  key={servicioId} 
                  label={servicio ? renderServicioLabel(servicio) : servicioId} 
                  size="small"
                  onDelete={() => handleDeleteServicio(servicioId)}
                  sx={{
                    backgroundColor: 'rgb(70, 120, 195)',
                    color: '#fff',
                    fontWeight: 500,
                    '& .MuiChip-deleteIcon': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&:hover': {
                        color: '#fff'
                      }
                    }
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}
      
      {/* Botón para expandir/colapsar */}
      <Button
        variant="contained"
        onClick={() => setOpen(!open)}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        fullWidth
        disabled={disabled}
        style={{ 
          justifyContent: 'space-between', 
          textTransform: 'none',
          backgroundColor: 'rgb(70, 120, 195)',
          color: '#fff'
        }}
      >
        <span>Seleccionar Servicios</span>
        <span style={{ 
          fontSize: '12px', 
          color: '#fff',
          fontWeight: '500'
        }}>
          {selectedArray.length} seleccionados
        </span>
      </Button>
      
      {/* Contenido expandible */}
      <Collapse in={open}>
        <Box sx={{ 
          mt: 1, 
          p: 2, 
          border: '1px solid #ddd', 
          borderRadius: 1,
          backgroundColor: '#fff'
        }}>
                     {/* Campo de búsqueda */}
           <TextField
             fullWidth
             placeholder="Buscar servicios por nombre, localización..."
             value={searchTerm}
             onChange={handleSearchChange}
             InputProps={{
               startAdornment: (
                 <InputAdornment position="start">
                   <SearchIcon />
                 </InputAdornment>
               ),
               endAdornment: searchTerm && (
                 <InputAdornment position="end">
                   <IconButton
                     size="small"
                     onClick={handleClearSearch}
                     edge="end"
                     sx={{
                       color: 'rgba(0, 0, 0, 0.54)',
                       backgroundColor: 'transparent',
                       borderRadius: '50%',
                       width: 28,
                       height: 28,
                       padding: 0,
                       '&:hover': {
                         backgroundColor: 'rgba(0, 0, 0, 0.04)',
                         color: 'rgba(0, 0, 0, 0.87)'
                       }
                     }}
                   >
                     <CloseIcon sx={{ fontSize: 18 }} />
                   </IconButton>
                 </InputAdornment>
               )
             }}
             size="small"
             style={{ marginBottom: '12px' }}
             sx={{
               '& .MuiInputBase-input::placeholder': {
                 color: '#666',
                 opacity: 1,
                 fontSize: '14px'
               },
               '& .MuiInputBase-root': {
                 backgroundColor: '#fff',
                 borderRadius: '4px'
               },
               '& .MuiOutlinedInput-root': {
                 '& fieldset': {
                   borderColor: '#ddd'
                 },
                 '&:hover fieldset': {
                   borderColor: 'rgb(70, 120, 195)'
                 },
                 '&.Mui-focused fieldset': {
                   borderColor: 'rgb(70, 120, 195)'
                 }
               }
             }}
           />
          
          {/* Toggle de visualización */}
          {showDisplayToggle && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Mostrar:
              </Typography>
              <ToggleButtonGroup
                value={displayMode}
                exclusive
                onChange={handleDisplayModeChange}
                size="small"
                sx={{ gap: 1 }}
              >
                <ToggleButton 
                  value="localizacion" 
                  sx={{ 
                    textTransform: 'none', 
                    px: 2, 
                    py: 0.5,
                    borderRadius: '6px !important',
                    border: '1px solid #d0d5dd !important',
                    '&.Mui-selected': {
                      backgroundColor: 'rgb(70, 120, 195)',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: 'rgb(55, 100, 170)'
                      }
                    }
                  }}
                >
                  Localización
                </ToggleButton>
                <ToggleButton 
                  value="nombre" 
                  sx={{ 
                    textTransform: 'none', 
                    px: 2, 
                    py: 0.5,
                    borderRadius: '6px !important',
                    border: '1px solid #d0d5dd !important',
                    '&.Mui-selected': {
                      backgroundColor: 'rgb(70, 120, 195)',
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: 'rgb(55, 100, 170)'
                      }
                    }
                  }}
                >
                  Nombre
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Información de resultados */}
          <Box sx={{ 
            mb: 2, 
            p: 1, 
            backgroundColor: '#f5f5f5', 
            borderRadius: 1,
            fontSize: '12px',
            color: '#666'
          }}>
            {searchTerm 
              ? `${filteredServicios.length} servicios encontrados • ${selectedArray.length} seleccionados`
              : `${servicios.length} servicios disponibles • ${selectedArray.length} seleccionados`
            }
          </Box>
          
          {/* Lista de servicios */}
          <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredServicios.length > 0 ? (
              <>
                {filteredServicios.map((servicio) => {
                  const servicioId = servicio.numTienda.toString();
                  const isSelected = selectedArray.includes(servicioId);
                  
                  return (
                    <Box key={servicioId} sx={{ 
                      p: 1, 
                      borderBottom: '1px solid #eee',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: isSelected ? 'rgba(70, 120, 195, 0.08)' : 'transparent',
                      borderLeft: isSelected ? '3px solid rgb(70, 120, 195)' : 'none'
                    }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => handleServicioToggle(servicioId, e.target.checked)}
                        size="small"
                        sx={{
                          color: 'rgb(70, 120, 195)',
                          '&.Mui-checked': {
                            color: 'rgb(70, 120, 195)'
                          }
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ 
                          fontWeight: isSelected ? '500' : 'normal',
                          color: isSelected ? 'rgb(70, 120, 195)' : 'inherit'
                        }}>
                          {renderServicioLabel(servicio)}
                        </Typography>
                        {isSelected && (
                          <Typography variant="caption" sx={{ 
                            color: 'rgb(70, 120, 195)',
                            display: 'block',
                            mt: 0.5
                          }}>
                            ✓ Seleccionado
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                
                {/* Loading indicator para más datos */}
                {isLoadingMore && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CircularProgress size={20} />
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Cargando más servicios...
                    </Typography>
                  </Box>
                )}
                
                {/* Observer target para infinite scroll */}
                {hasMore && !isLoadingMore && (
                  <div ref={observerRef} style={{ height: '20px' }} />
                )}
                
                {/* Mensaje de fin */}
                {!hasMore && filteredServicios.length > 0 && (
                  <Box sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    color: '#666',
                    fontSize: '12px',
                    borderTop: '1px solid #eee',
                    mt: 1
                  }}>
                    No hay más servicios que mostrar
                  </Box>
                )}
              </>
                         ) : (
               <Box sx={{ 
                 p: 3, 
                 textAlign: 'center', 
                 color: '#666',
                 fontSize: '14px'
               }}>
                 {searchTerm 
                   ? `No se encontraron servicios que coincidan con "${searchTerm}"`
                   : "Busca servicios para ver los disponibles"
                 }
               </Box>
             )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ServicioSearch;